'use client'

/* eslint-disable @typescript-eslint/no-explicit-any, max-lines-per-function */
// Audit timeline requires flexible event data handling and comprehensive display logic

import { useState, useMemo } from 'react'
import { Clock, User, Filter, ChevronDown, ChevronUp } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

/**
 * BIRHAUS AuditTimeline
 * 
 * Implements BIRHAUS principle #10: "Auditability"
 * - Complete audit trail with Spanish-first descriptions
 * - Progressive disclosure of event details 
 * - Filterable timeline with real-time search
 * - Color-coded actions for quick visual scanning
 * - Expandable payload details for technical users
 * - Automatic relative time formatting in Spanish
 */

// BIRHAUS: Utility function (replaces @/lib/utils cn)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export type AuditEvent = {
  id: string
  actor_id: string
  actor_name?: string
  verb: string
  entity: string | null
  entity_id: string | null
  payload: Record<string, any>
  request_id: string | null
  created_at: string
}

type AuditTimelineProps = {
  events: AuditEvent[]
  showFilters?: boolean
  maxHeight?: string
  className?: string
  // BIRHAUS: Spanish-first customization
  emptyMessage?: string
  filterAllEntitiesLabel?: string
  filterAllActionsLabel?: string
  clearFiltersLabel?: string
  showDetailsLabel?: string
  hideDetailsLabel?: string
}

// BIRHAUS: Spanish-first verb descriptions
const VERB_DESCRIPTIONS: Record<string, string> = {
  'iglesia.creada': 'creó una iglesia',
  'iglesia.actualizada': 'actualizó una iglesia',
  'iglesia.eliminada': 'eliminó una iglesia',
  'pastor.creado': 'registró un pastor',
  'pastor.actualizado': 'actualizó un pastor',
  'pastor.eliminado': 'eliminó un pastor',
  'donacion.registrada': 'registró una donación',
  'donacion.anulada': 'anuló una donación',
  'transferencia.creada': 'creó una transferencia',
  'transferencia.completada': 'completó una transferencia',
  'gasto.registrado': 'registró un gasto',
  'gasto.aprobado': 'aprobó un gasto',
  'gasto.rechazado': 'rechazó un gasto',
  'usuario.login': 'inició sesión',
  'usuario.logout': 'cerró sesión',
  'resumen.enviado': 'envió un resumen',
  'resumen.aprobado': 'aprobó un resumen',
  'resumen.rechazado': 'rechazó un resumen',
  // Generic fallbacks
  'create': 'creó',
  'update': 'actualizó', 
  'delete': 'eliminó',
  'approve': 'aprobó',
  'reject': 'rechazó',
  'login': 'inició sesión',
  'logout': 'cerró sesión'
}

// BIRHAUS: Semantic color coding for actions
const ACTION_COLORS: Record<string, string> = {
  creada: 'text-green-600',
  creado: 'text-green-600',
  registrada: 'text-green-600',
  registrado: 'text-green-600',
  actualizada: 'text-blue-600',
  actualizado: 'text-blue-600',
  eliminada: 'text-red-600',
  eliminado: 'text-red-600',
  anulada: 'text-yellow-600',
  aprobado: 'text-green-600',
  rechazado: 'text-red-600',
  completada: 'text-green-600',
  enviado: 'text-blue-600',
  login: 'text-gray-500',
  logout: 'text-gray-500',
}

export function AuditTimeline({
  events,
  showFilters = true,
  maxHeight = '600px',
  className = '',
  emptyMessage = 'No hay eventos que mostrar',
  filterAllEntitiesLabel = 'Todas las entidades',
  filterAllActionsLabel = 'Todas las acciones',
  clearFiltersLabel = 'Limpiar filtros',
  showDetailsLabel = 'Ver detalles',
  hideDetailsLabel = 'Ocultar detalles'
}: AuditTimelineProps) {
  const [filterEntity, setFilterEntity] = useState<string>('all')
  const [filterVerb, setFilterVerb] = useState<string>('all')
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set())

  // BIRHAUS: Extract unique entities and verbs for progressive filtering
  const { entities, verbs } = useMemo(() => {
    const uniqueEntities = new Set<string>()
    const uniqueVerbs = new Set<string>()
    
    events.forEach(event => {
      if (event.entity) uniqueEntities.add(event.entity)
      uniqueVerbs.add(event.verb)
    })
    
    return {
      entities: Array.from(uniqueEntities).sort(),
      verbs: Array.from(uniqueVerbs).sort()
    }
  }, [events])

  // Filter events based on selected criteria
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (filterEntity !== 'all' && event.entity !== filterEntity) return false
      if (filterVerb !== 'all' && event.verb !== filterVerb) return false
      return true
    })
  }, [events, filterEntity, filterVerb])

  // Toggle expanded state for event details
  const toggleExpanded = (eventId: string) => {
    const newExpanded = new Set(expandedEvents)
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId)
    } else {
      newExpanded.add(eventId)
    }
    setExpandedEvents(newExpanded)
  }

  // Get semantic color based on verb action
  const getActionColor = (verb: string) => {
    const lastPart = verb.split('.').pop() || ''
    // eslint-disable-next-line security/detect-object-injection -- Safe access to static color mapping object
    return ACTION_COLORS[lastPart] || 'text-gray-700'
  }

  // BIRHAUS: Format payload with progressive disclosure
  const formatPayload = (payload: Record<string, any>) => {
    if (!payload || Object.keys(payload).length === 0) return null
    
    return (
      <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs space-y-1 border">
        {Object.entries(payload).map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <span className="text-gray-500 font-medium min-w-20">{key}:</span>
            <span className="text-gray-700 flex-1">
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)} data-birhaus-component="audit-timeline">
      {/* BIRHAUS: Progressive disclosure filters */}
      {showFilters && (entities.length > 0 || verbs.length > 0) && (
        <div className="flex gap-3 items-center p-3 bg-gray-50 border border-gray-200 rounded-md">
          <Filter className="h-4 w-4 text-gray-400" />
          
          {entities.length > 0 && (
            <select
              value={filterEntity}
              onChange={(e) => setFilterEntity(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{filterAllEntitiesLabel}</option>
              {entities.map(entity => (
                <option key={entity} value={entity}>
                  {entity.charAt(0).toUpperCase() + entity.slice(1)}
                </option>
              ))}
            </select>
          )}
          
          {verbs.length > 0 && (
            <select
              value={filterVerb}
              onChange={(e) => setFilterVerb(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{filterAllActionsLabel}</option>
              {verbs.map(verb => (
                <option key={verb} value={verb}>
                  {/* eslint-disable-next-line security/detect-object-injection -- Safe access to static verb mapping object */}
                  {VERB_DESCRIPTIONS[verb] || verb}
                </option>
              ))}
            </select>
          )}
          
          {(filterEntity !== 'all' || filterVerb !== 'all') && (
            <button
              onClick={() => {
                setFilterEntity('all')
                setFilterVerb('all')
              }}
              className="text-sm text-primary-600 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 px-2 py-1 rounded"
            >
              {clearFiltersLabel}
            </button>
          )}
        </div>
      )}

      {/* BIRHAUS: Audit timeline with visual hierarchy */}
      <div 
        className="space-y-2 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        style={{ maxHeight }}
      >
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          filteredEvents.map((event, index) => {
            const isExpanded = expandedEvents.has(event.id)
            const hasPayload = event.payload && Object.keys(event.payload).length > 0
            
            return (
              <div
                key={event.id}
                className="flex gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-150"
              >
                {/* BIRHAUS: Visual timeline indicator */}
                <div className="flex flex-col items-center">
                  <div className="w-2 h-2 rounded-full bg-primary-600 mt-2 flex-shrink-0" />
                  {index < filteredEvents.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200 mt-2" />
                  )}
                </div>

                {/* Event content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <User className="h-3 w-3 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {event.actor_name || 'Usuario'}
                        </span>
                        <span className={cn('text-sm font-medium', getActionColor(event.verb))}>
                          {VERB_DESCRIPTIONS[event.verb] || event.verb}
                        </span>
                        {event.entity && (
                          <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
                            {event.entity}
                          </span>
                        )}
                      </div>
                      
                      {event.entity_id && (
                        <div className="text-xs text-gray-500 mt-1 font-mono">
                          ID: {event.entity_id}
                        </div>
                      )}
                      
                      {/* BIRHAUS: Progressive disclosure for technical details */}
                      {hasPayload && (
                        <button
                          onClick={() => toggleExpanded(event.id)}
                          className="flex items-center gap-1 mt-2 text-xs text-primary-600 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 px-1 py-0.5 rounded"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="h-3 w-3" />
                              {hideDetailsLabel}
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3" />
                              {showDetailsLabel}
                            </>
                          )}
                        </button>
                      )}
                      
                      {isExpanded && formatPayload(event.payload)}
                    </div>
                    
                    {/* BIRHAUS: Spanish-first time formatting */}
                    <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                      <Clock className="h-3 w-3" />
                      <span title={new Date(event.created_at).toLocaleString('es-ES')}>
                        {formatDistanceToNow(new Date(event.created_at), { 
                          addSuffix: true,
                          locale: es 
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// BIRHAUS: Hook for audit event management
export function useAuditTimeline() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  
  const addEvent = (event: Omit<AuditEvent, 'id' | 'created_at'>) => {
    const newEvent: AuditEvent = {
      ...event,
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: new Date().toISOString()
    }
    
    setEvents(prev => [newEvent, ...prev])
    return newEvent
  }
  
  const clearEvents = () => {
    setEvents([])
  }
  
  return {
    events,
    addEvent,
    clearEvents
  }
}

// Components and hooks are exported via individual export declarations above